const API_URL = process.env.REACT_APP_BACKEND_URL;

import { secureStorage } from '../utils/secureStorage';

// Helper to get auth header - checks both admin token and employee portal token
const getAuthHeader = () => {
  const token = secureStorage.getItem('token') || secureStorage.getItem('employee_portal_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Generic fetch wrapper
const fetchApi = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Auth
export const authApi = {
  login: (email, password) => fetchApi('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  
  getMe: () => fetchApi('/auth/me'),
  
  register: (userData) => fetchApi('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  
  changePassword: (currentPassword, newPassword) => fetchApi('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  }),

  updateProfile: (data) => fetchApi('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Users
export const usersApi = {
  getAll: () => fetchApi('/users'),
  getFormer: () => fetchApi('/users/former'),
  restore: (id) => fetchApi(`/users/${id}/restore`, { method: 'POST' }),
  getById: (id) => fetchApi(`/users/${id}`),
  create: (userData) => fetchApi('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  update: (id, userData) => fetchApi(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),
  delete: (id) => fetchApi(`/users/${id}`, { method: 'DELETE' }),
  resetPin: (id, pin = '1234') => fetchApi(`/users/${id}/reset-pin`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  }),
};

// Employees
export const employeesApi = {
  getAll: (locationId = null) => {
    const params = locationId && locationId !== 'all' ? `?location_id=${locationId}` : '';
    return fetchApi(`/employees${params}`);
  },
  getById: (id) => fetchApi(`/employees/${id}`),
  getByNumber: (number) => fetchApi(`/employees/number/${number}`),
  create: (employeeData) => fetchApi('/employees', {
    method: 'POST',
    body: JSON.stringify(employeeData),
  }),
  update: (id, employeeData) => fetchApi(`/employees/${id}`, {
    method: 'PUT',
    body: JSON.stringify(employeeData),
  }),
  delete: (id) => fetchApi(`/employees/${id}`, { method: 'DELETE' }),
  terminate: (id) => fetchApi(`/employees/${id}`, { method: 'DELETE' }), // Same as delete - soft delete
  reinstate: (id, reason = '') => fetchApi(`/employees/${id}/reinstate`, { 
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  terminateWithDetails: (id, details) => fetchApi(`/employees/${id}/terminate`, {
    method: 'POST',
    body: JSON.stringify(details),
  }),
  getFormer: (locationId = null) => {
    const query = locationId && locationId !== 'all' ? `?location_id=${locationId}` : '';
    return fetchApi(`/employees/former/list${query}`);
  },
  setPasscode: (employeeNumber, passcode, currentPasscode = null) => fetchApi('/employees/set-passcode', {
    method: 'POST',
    body: JSON.stringify({ employee_number: employeeNumber, passcode, current_passcode: currentPasscode }),
  }),
  resetPasscode: (employeeId, passcode, reason = '') => fetchApi(`/employees/${employeeId}/reset-passcode`, {
    method: 'POST',
    body: JSON.stringify({ passcode, reason }),
  }),
  verifyPasscode: (employeeNumber, passcode) => fetchApi('/employees/verify-passcode', {
    method: 'POST',
    body: JSON.stringify({ employee_number: employeeNumber, passcode }),
  }),
  promote: (id, data) => fetchApi(`/employees/${id}/promote`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Locations
export const locationsApi = {
  getAll: () => fetchApi('/locations'),
  getPublic: () => fetch(`${API_URL}/api/locations/public`).then(r => r.json()),
  getById: (id) => fetchApi(`/locations/${id}`),
  create: (locationData) => fetchApi('/locations', {
    method: 'POST',
    body: JSON.stringify(locationData),
  }),
  update: (id, locationData) => fetchApi(`/locations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(locationData),
  }),
  delete: (id) => fetchApi(`/locations/${id}`, { method: 'DELETE' }),
  getFormer: () => fetchApi('/locations/former'),
  restore: (id) => fetchApi(`/locations/${id}/restore`, { method: 'POST' }),
  getAllForTransfer: () => fetchApi('/locations/all'),
};

// Schedules
export const schedulesApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') query.append('location_id', params.locationId);
    if (params.employeeId) query.append('employee_id', params.employeeId);
    if (params.startDate) query.append('start_date', params.startDate);
    if (params.endDate) query.append('end_date', params.endDate);
    const queryStr = query.toString();
    return fetchApi(`/schedules${queryStr ? `?${queryStr}` : ''}`);
  },
  getById: (id) => fetchApi(`/schedules/${id}`),
  create: (scheduleData) => fetchApi('/schedules', {
    method: 'POST',
    body: JSON.stringify(scheduleData),
  }),
  update: (id, scheduleData) => fetchApi(`/schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(scheduleData),
  }),
  delete: (id) => fetchApi(`/schedules/${id}`, { method: 'DELETE' }),
  copyWeek: (copyData) => fetchApi('/schedules/copy-week', {
    method: 'POST',
    body: JSON.stringify(copyData),
  }),
  publish: (locationId, startDate, endDate) => fetchApi('/schedules/publish', {
    method: 'POST',
    body: JSON.stringify({ location_id: locationId, start_date: startDate, end_date: endDate }),
  }),
  unpublish: (locationId, startDate, endDate) => fetchApi('/schedules/unpublish', {
    method: 'POST',
    body: JSON.stringify({ location_id: locationId, start_date: startDate, end_date: endDate }),
  }),
  getOpenShifts: (locationId, startDate, endDate) => {
    const params = new URLSearchParams({ location_id: locationId });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return fetchApi(`/schedules/open-shifts?${params}`);
  },
  claimShift: (scheduleId) => fetchApi('/schedules/claim-shift', {
    method: 'POST',
    body: JSON.stringify({ schedule_id: scheduleId }),
  }),
  reviewClaim: (claimId, status) => fetchApi(`/schedules/review-claim/${claimId}`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  }),
  getPendingClaims: (locationId) => {
    const params = locationId ? `?location_id=${locationId}` : '';
    return fetchApi(`/schedules/pending-claims${params}`);
  },
};

// Time Entries
export const timeEntriesApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') query.append('location_id', params.locationId);
    if (params.employeeId) query.append('employee_id', params.employeeId);
    if (params.startDate) query.append('start_date', params.startDate);
    if (params.endDate) query.append('end_date', params.endDate);
    const queryStr = query.toString();
    return fetchApi(`/time-entries${queryStr ? `?${queryStr}` : ''}`);
  },
  getById: (id) => fetchApi(`/time-entries/${id}`),
  clockIn: (employeeNumber, passcode, photo, cameraUnavailable = false) => fetchApi('/time-entries/clock-in', {
    method: 'POST',
    body: JSON.stringify({ employee_number: employeeNumber, passcode, photo, camera_unavailable: cameraUnavailable }),
  }),
  clockOut: (employeeNumber, passcode, photo, cameraUnavailable = false) => fetchApi('/time-entries/clock-out', {
    method: 'POST',
    body: JSON.stringify({ employee_number: employeeNumber, passcode, photo, camera_unavailable: cameraUnavailable }),
  }),
  update: (id, entryData) => fetchApi(`/time-entries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(entryData),
  }),
  delete: (id) => fetchApi(`/time-entries/${id}`, { method: 'DELETE' }),
  startBreak: (employeeNumber, passcode) => fetchApi('/time-entries/break-start', {
    method: 'POST',
    body: JSON.stringify({ employee_number: employeeNumber, passcode }),
  }),
  endBreak: (employeeNumber, passcode) => fetchApi('/time-entries/break-end', {
    method: 'POST',
    body: JSON.stringify({ employee_number: employeeNumber, passcode }),
  }),
  getActiveEntry: (employeeNumber) => fetchApi(`/time-entries/active/${employeeNumber}`),
  createManual: (data) => fetchApi('/time-entries/manual', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Time Off
export const timeOffApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') query.append('location_id', params.locationId);
    if (params.status) query.append('status', params.status);
    const queryStr = query.toString();
    return fetchApi(`/time-off${queryStr ? `?${queryStr}` : ''}`);
  },
  getById: (id) => fetchApi(`/time-off/${id}`),
  getByEmployee: (employeeId) => fetchApi(`/time-off?employee_id=${employeeId}`),
  create: (requestData) => fetchApi('/time-off', {
    method: 'POST',
    body: JSON.stringify(requestData),
  }),
  review: (id, status, reviewNote = null, overrideOpts = {}) => fetchApi(`/time-off/${id}/review`, {
    method: 'PUT',
    body: JSON.stringify({
      status,
      review_note: reviewNote,
      override: overrideOpts.override || false,
      override_reason: overrideOpts.override_reason || null,
    }),
  }),
  delete: (id) => fetchApi(`/time-off/${id}`, { method: 'DELETE' }),
  // Blackouts
  listBlackouts: (locationId) => {
    const q = locationId && locationId !== 'all' ? `?location_id=${locationId}` : '';
    return fetchApi(`/time-off/blackouts${q}`);
  },
  createBlackout: (data) => fetchApi('/time-off/blackouts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteBlackout: (id) => fetchApi(`/time-off/blackouts/${id}`, { method: 'DELETE' }),
  // Limits
  listLimits: (locationId) => {
    const q = locationId && locationId !== 'all' ? `?location_id=${locationId}` : '';
    return fetchApi(`/time-off/limits${q}`);
  },
  upsertLimit: (data) => fetchApi('/time-off/limits', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteLimit: (id) => fetchApi(`/time-off/limits/${id}`, { method: 'DELETE' }),
  // Precheck (used in request form)
  precheck: (params) => {
    const q = new URLSearchParams();
    if (params.locationId) q.append('location_id', params.locationId);
    q.append('start_date', params.startDate);
    q.append('end_date', params.endDate);
    return fetchApi(`/time-off/precheck?${q.toString()}`);
  },
};

// Messages
export const messagesApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') query.append('location_id', params.locationId);
    if (params.messageType) query.append('message_type', params.messageType);
    if (params.conversationId) query.append('conversation_id', params.conversationId);
    const queryStr = query.toString();
    return fetchApi(`/messages${queryStr ? `?${queryStr}` : ''}`);
  },
  getById: (id) => fetchApi(`/messages/${id}`),
  getRecipients: () => fetchApi('/messages/recipients'),
  create: (messageData) => fetchApi('/messages', {
    method: 'POST',
    body: JSON.stringify(messageData),
  }),
  markAsRead: (id) => fetchApi(`/messages/${id}/read`, { method: 'POST' }),
  markAllRead: () => fetchApi('/messages/mark-all-read', { method: 'POST' }),
  delete: (id) => fetchApi(`/messages/${id}`, { method: 'DELETE' }),
};

// Payroll
export const payrollApi = {
  getSummary: (params = {}) => {
    const query = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') query.append('location_id', params.locationId);
    if (params.period) query.append('period', params.period);
    if (params.includeManagerSalaries) query.append('include_manager_salaries', 'true');
    if (params.startDate) query.append('start_date', params.startDate);
    if (params.endDate) query.append('end_date', params.endDate);
    const queryStr = query.toString();
    return fetchApi(`/payroll${queryStr ? `?${queryStr}` : ''}`);
  },
};

// Audit Logs
export const auditLogsApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.entityType) query.append('entity_type', params.entityType);
    if (params.userId) query.append('user_id', params.userId);
    if (params.limit) query.append('limit', params.limit);
    const queryStr = query.toString();
    return fetchApi(`/audit-logs${queryStr ? `?${queryStr}` : ''}`);
  },
  getForEntity: (entityType, entityId) => fetchApi(`/audit-logs/${entityType}/${entityId}`),
  listCodeUpdates: (limit = 100) => fetchApi(`/audit-logs/code-updates/list?limit=${limit}`),
  createCodeUpdate: (data) => fetchApi('/audit-logs/code-updates', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteCodeUpdate: (id) => fetchApi(`/audit-logs/code-updates/${id}`, { method: 'DELETE' }),
};

// Employee Availability
export const availabilityApi = {
  getMine: () => fetchApi('/availability/me'),
  updateMine: (data) => fetchApi('/availability/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  list: (locationId) => {
    const q = locationId && locationId !== 'all' ? `?location_id=${locationId}` : '';
    return fetchApi(`/availability${q}`);
  },
  getEmployee: (employeeId) => fetchApi(`/availability/${employeeId}`),
};

// Shift requests (trade / cover)
export const shiftRequestsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.append('status', params.status);
    if (params.locationId && params.locationId !== 'all') q.append('location_id', params.locationId);
    if (params.employeeId) q.append('employee_id', params.employeeId);
    const s = q.toString();
    return fetchApi(`/shift-requests${s ? `?${s}` : ''}`);
  },
  create: (data) => fetchApi('/shift-requests', { method: 'POST', body: JSON.stringify(data) }),
  review: (id, status, reviewNote = null) => fetchApi(`/shift-requests/${id}/review`, {
    method: 'PUT',
    body: JSON.stringify({ status, review_note: reviewNote }),
  }),
  cancel: (id) => fetchApi(`/shift-requests/${id}`, { method: 'DELETE' }),
};

// Reports
export const reportsApi = {
  employee: (params) => {
    const q = new URLSearchParams();
    q.append('start_date', params.startDate);
    q.append('end_date', params.endDate);
    if (params.locationId && params.locationId !== 'all') q.append('location_id', params.locationId);
    if (params.employeeId && params.employeeId !== 'all') q.append('employee_id', params.employeeId);
    return fetchApi(`/reports/employee?${q.toString()}`);
  },
  location: (params) => {
    const q = new URLSearchParams();
    q.append('start_date', params.startDate);
    q.append('end_date', params.endDate);
    if (params.locationId && params.locationId !== 'all') q.append('location_id', params.locationId);
    return fetchApi(`/reports/location?${q.toString()}`);
  },
};

// Notifications
export const notificationsApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.limit) query.append('limit', params.limit);
    if (params.unreadOnly) query.append('unread_only', 'true');
    const queryStr = query.toString();
    return fetchApi(`/notifications${queryStr ? `?${queryStr}` : ''}`);
  },
  markAsRead: (id) => fetchApi(`/notifications/${id}/read`, { method: 'POST' }),
  markAllAsRead: () => fetchApi('/notifications/mark-all-read', { method: 'POST' }),
  clearAll: () => fetchApi('/notifications/clear-all', { method: 'DELETE' }),
};

// Transfers
export const transfersApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.append('status', params.status);
    const queryStr = query.toString();
    return fetchApi(`/transfers${queryStr ? `?${queryStr}` : ''}`);
  },
  getPending: () => fetchApi('/transfers/pending'),
  create: (transferData) => fetchApi('/transfers', {
    method: 'POST',
    body: JSON.stringify(transferData),
  }),
  review: (id, reviewData) => fetchApi(`/transfers/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(reviewData),
  }),
  cancel: (id) => fetchApi(`/transfers/${id}`, { method: 'DELETE' }),
};

// Stations
export const stationsApi = {
  getAll: (includeInactive = false) => {
    const query = includeInactive ? '?include_inactive=true' : '';
    return fetchApi(`/stations${query}`);
  },
  create: (data) => fetchApi('/stations', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchApi(`/stations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchApi(`/stations/${id}`, { method: 'DELETE' }),
  reactivate: (id) => fetchApi(`/stations/${id}/reactivate`, { method: 'POST' }),
};

// Schedule Notes
export const scheduleNotesApi = {
  list: ({ locationId, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    if (locationId) params.append('location_id', locationId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return fetchApi(`/schedule-notes?${params.toString()}`);
  },
  upsert: (data) => fetchApi('/schedule-notes', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// File uploads
export const uploadsApi = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/uploads`, {
      method: 'POST',
      headers: { ...getAuthHeader() }, // do NOT set Content-Type — browser sets multipart boundary
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(err.detail || `Upload failed (${response.status})`);
    }
    return response.json();
  },
  downloadUrl: (fileId) => {
    const token = secureStorage.getItem('token') || secureStorage.getItem('employee_portal_token');
    return `${API_URL}/api/uploads/${fileId}?auth=${encodeURIComponent(token || '')}`;
  },
  info: (fileId) => fetchApi(`/uploads/${fileId}/info`),
};

export default {
  auth: authApi,
  users: usersApi,
  employees: employeesApi,
  locations: locationsApi,
  schedules: schedulesApi,
  timeEntries: timeEntriesApi,
  timeOff: timeOffApi,
  messages: messagesApi,
  payroll: payrollApi,
  auditLogs: auditLogsApi,
  transfers: transfersApi,
  stations: stationsApi,
  scheduleNotes: scheduleNotesApi,
  uploads: uploadsApi,
};
